// Import necessary dependencies from React and Material-UI
import React, { useEffect, useState } from 'react';
import './Coin.css';
import coinMarketData from '../../../services/coinMarketData';
import { useParams } from 'react-router-dom';
import { setChartOneData } from '../../../functions/setChartData';
import LineChart from '../../chart/LineChart/LineChart';
import Container from '@mui/material/Container';
import Header from '../../common/Header/Header';
import List from '../../dashboard/List/List';
import SelectDays from '../../coin/SelectDays/SelectDays';
import ChartType from '../../coin/ChartType/ChartType';
import Grid2 from '@mui/material/Unstable_Grid2';
import Grid from '../../dashboard/Grid/Grid';
import { useSelector } from "react-redux";
import Footer from '../../common/Footer/Footer';
import scrollToTop from '../../../functions/scrollToTop';

/**
 * Coin component represents the page for displaying details of a specific cryptocurrency.
 * @returns {JSX.Element} - Coin component.
 */
const Coin = () => {
  // Extract the 'id' parameter from the URL using useParams hook
  const { id } = useParams();
  // Get the list of coins from the Redux store using useSelector
  const coins = useSelector((state) => state.coins);
  // State variables to manage loading states, coin data, chart data, selected days, and chart type
  const [isCoinLoading, setIsCoinLoading] = useState(true);
  const [isChartLoading, setIsChartLoading] = useState(true);
  const [coin, setCoin] = useState({});
  const [chart, setChart] = useState({});
  const [days, setDays] = useState(60);
  const [chartType, setChartType] = useState("prices");
  const [marketData, setMarketData] = useState({});

  // Set the document title based on the selected cryptocurrency
  document.title = `${id} | InsightCoin`;

  // Scroll to the top of the page when the component mounts
  useEffect(() => {
    scrollToTop();
  }, []);

  // Effect to fetch and set coin data when coins or id change
  useEffect(() => {
    if (coins.length > 0 && id) {
      let data = [...coins].filter((elem) => elem.id === id);
      setCoin(data[0]);
      setIsCoinLoading(false);
    }
  }, [coins, id]);

  // Effect to fetch and set coin market data when days or id change
  useEffect(() => {
    getCoinMarketData();
    async function getCoinMarketData() {
      const market = await coinMarketData(id, days);
      if (market) {
        setMarketData(market);
        setChartOneData(setChart, market[chartType], id);
        setIsChartLoading(false);
      }
    }
  }, [days, id, chartType]);

  // Event handler for changing the selected number of days
  const handleDaysChange = async (event) => {
    setDays(event.target.value);
  };

  // Event handler for changing the selected chart type
  const handleChartTypeChange = async (event) => {
    setChartType(event.target.value);
    setChartOneData(setChart, marketData[event.target.value], id);
  };

  // Render the JSX structure for the Coin component
  return (
    <>
      {/* Header component */}
      <Header />
      <br />

      {isCoinLoading ? (
        <></>
      ) : (
        <>
          {/* Display coin details in both list and grid views */}
          <Container maxWidth="lg">
            <div className="list-view-78">
              <List coin={coin} index={0} />
            </div>
            <div className="grid-view-78">
              <Grid coin={coin} index={0} />
            </div>
          </Container>
          <br />

          {/* Container for selecting days and chart type */}
          <Container maxWidth="lg">
            <Grid2 container spacing={0}>
              {/* SelectDays component for choosing the number of days */}
              <Grid2 xs={12} sm={3} md={3}>
                <SelectDays days={days} handleDaysChange={handleDaysChange} />
              </Grid2>
              {/* Placeholder Grid2 item */}
              <Grid2 xs={12} sm={1} md={3}></Grid2>
              {/* ChartType component for choosing the chart type */}
              <Grid2 xs={12} sm={8} md={6}>
                <br />
                <ChartType chartType={chartType} handleChartTypeChange={handleChartTypeChange} />
              </Grid2>
            </Grid2>
          </Container>
          <br />

          {/* Container for displaying the LineChart component */}
          <Container maxWidth="lg">
            {isChartLoading ? (
              <></>
            ) : (
              // LineChart component for visualizing market data
              <LineChart chartData={chart} chartType={chartType} />
            )}
          </Container>
          <br />
        </>
      )}

      {/* Footer component */}
      <Footer />
    </>
  );
};

// Export the Coin component as the default export of the module
export default Coin;
